import { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import PatientNav from './PatientNav';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { FaPlus, FaSearch, FaFileMedical,FaTimes, FaUserMd, FaCalendar, FaStethoscope } from 'react-icons/fa';
import Loader from '../components/Loader';

const Medical = () => {
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showMedicalDetails, setShowMedicalDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { nationalId } = useParams();

  const [newMedicalRecord, setNewMedicalRecord] = useState({
    date: '',
    diagnosis: '',
    treatment: '',
    notes: '',
    doctor: user?.id || '',
    patient: nationalId,
    chiefComplaint: '',
    vitalSigns: {
      bloodPressure: '',
      heartRate: '',
      temperature: '',
      weight: ''
    }
  });

  useEffect(() => {
    const fetchMedicalRecords = async () => {
      try {
        console.log('Fetching medical records for nationalId:', nationalId);
        // This endpoint needs to be created in the backend
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/medical-records/patient/${nationalId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Medical records fetched:', response.data);
        setMedicalRecords(response.data || []);
      } catch (err) {
        console.error('Error fetching medical records:', err.response?.data || err.message);
        // For now, set empty array if endpoint doesn't exist
        setMedicalRecords([]);
      } finally {
        setLoading(false);
      }
    };
    if (token && nationalId) fetchMedicalRecords();
  }, [nationalId, token]);

  const validateForm = () => {
    if (!newMedicalRecord.date) return 'Date is required';
    if (!newMedicalRecord.diagnosis) return 'Diagnosis is required';
    if (!newMedicalRecord.treatment) return 'Treatment is required';
    if (!newMedicalRecord.chiefComplaint) return 'Chief complaint is required';
    return '';
  };

  const handleAddMedicalRecord = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      setError('You must be logged in as a dentist to add medical records.');
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      console.log('Creating medical record:', newMedicalRecord);
      // This endpoint needs to be created in the backend
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/medical-records`, newMedicalRecord, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Medical record created:', response.data);
      setMedicalRecords([response.data, ...medicalRecords]);
      setNewMedicalRecord({
        date: '',
        diagnosis: '',
        treatment: '',
        notes: '',
        doctor: user.id,
        patient: nationalId,
        chiefComplaint: '',
        vitalSigns: {
          bloodPressure: '',
          heartRate: '',
          temperature: '',
          weight: ''
        }
      });
      setShowAddForm(false);
      setError('');
    } catch (err) {
      console.error('Error creating medical record:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Failed to add medical record');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('vitalSigns.')) {
      const vitalSign = name.split('.')[1];
      setNewMedicalRecord(prev => ({
        ...prev,
        vitalSigns: {
          ...prev.vitalSigns,
          [vitalSign]: value
        }
      }));
    } else {
      setNewMedicalRecord({ ...newMedicalRecord, [name]: value });
    }
  };

  const filteredMedicalRecords = medicalRecords.filter((record) =>
    record.diagnosis.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.treatment.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateValue) => {
    try {
      let date;
      if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue && dateValue.toDate) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (dateValue && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else {
        return 'Date not available';
      }
      
      if (isNaN(date.getTime())) {
        return 'Date not available';
      }
      
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date not available';
    }
  };

  const deleteMedicalRecord = async (id) => {
    try {
      console.log('Deleting medical record:', id);
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/medical-records/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMedicalRecords(medicalRecords.filter((record) => record._id !== id));
      setShowMedicalDetails(null);
    } catch (err) {
      console.error('Error deleting medical record:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Failed to delete medical record');
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (!user || user.role !== 'dentist') {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[#0077B6]/5 to-white">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center max-w-md bg-white p-8 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="text-[#0077B6] mb-4">
                  <FaStethoscope className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Access Denied</h3>
                <p className="text-gray-600 mb-6">Please log in as a dentist to view this page.</p>
              </motion.div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <PatientNav />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[#0077B6]/5 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-6 p-4 bg-red-100 text-[#333333] rounded-lg"
              >
                {error}
              </motion.div>
            )}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-1">
                    Medical Records
                  </h1>
                  <p className="text-gray-600">Manage patient medical information</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAddForm(true)}
                  className="w-full md:w-auto bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white px-6 py-2.5 rounded-full font-medium hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                >
                  <FaPlus className="h-5 w-5 mr-2" />
                  New Medical Record
                </motion.button>
              </div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative mb-6"
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search medical records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-full bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0077B6] focus:bg-white sm:text-sm transition-all duration-200"
                />
              </motion.div>
              <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                {filteredMedicalRecords.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-8 text-center"
                  >
                    <div className="mx-auto h-16 w-16 text-gray-400 mb-4">
                      <FaFileMedical className="h-16 w-16" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">No medical records found</h3>
                    <p className="text-gray-500">
                      {searchTerm ? 'Try a different search term' : 'Get started by adding a new medical record'}
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowAddForm(true)}
                      className="mt-6 inline-flex items-center px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 font-medium shadow-md hover:shadow-lg"
                    >
                      <FaPlus className="h-5 w-5 mr-2" />
                      Add Medical Record
                    </motion.button>
                  </motion.div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {filteredMedicalRecords.map((record) => (
                      <motion.li
                        key={record._id}
                        variants={item}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => setShowMedicalDetails(record)}
                      >
                        <div className="px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-3 w-3 rounded-full bg-[#0077B6]"></div>
                              <h3 className="text-lg font-medium text-gray-800">{record.diagnosis}</h3>
                            </div>
                            <div className="text-sm text-gray-500">{formatDate(record.date)}</div>
                          </div>
                          <div className="mt-2 flex justify-between items-center">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Treatment:</span> {record.treatment}
                            </p>
                          </div>
                        </div>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </motion.div>
            </motion.div>
            {showAddForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
                >
                  <div className="p-6 border-b flex justify-between items-center bg-[#0077B6]/10">
                    <h2 className="text-xl font-semibold text-[#0077B6]">New Medical Record</h2>
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes className="h-6 w-6" />
                    </button>
                  </div>
                  <form onSubmit={handleAddMedicalRecord} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Date*</label>
                        <div className="relative">
                          <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="date"
                            name="date"
                            value={newMedicalRecord.date}
                            onChange={handleInputChange}
                            className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Chief Complaint*</label>
                        <input
                          type="text"
                          name="chiefComplaint"
                          value={newMedicalRecord.chiefComplaint}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Diagnosis*</label>
                      <textarea
                        name="diagnosis"
                        value={newMedicalRecord.diagnosis}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        rows="3"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Treatment*</label>
                      <textarea
                        name="treatment"
                        value={newMedicalRecord.treatment}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        rows="3"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes (Optional)</label>
                      <textarea
                        name="notes"
                        value={newMedicalRecord.notes}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        rows="3"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Blood Pressure</label>
                        <input
                          type="text"
                          name="vitalSigns.bloodPressure"
                          value={newMedicalRecord.vitalSigns.bloodPressure}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                          placeholder="e.g., 120/80"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Heart Rate</label>
                        <input
                          type="text"
                          name="vitalSigns.heartRate"
                          value={newMedicalRecord.vitalSigns.heartRate}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                          placeholder="e.g., 72 bpm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Temperature</label>
                        <input
                          type="text"
                          name="vitalSigns.temperature"
                          value={newMedicalRecord.vitalSigns.temperature}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                          placeholder="e.g., 98.6°F"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Weight</label>
                        <input
                          type="text"
                          name="vitalSigns.weight"
                          value={newMedicalRecord.vitalSigns.weight}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                          placeholder="e.g., 70 kg"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-4">
                      <motion.button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        type="submit"
                        className="px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 font-medium transition-colors shadow-md hover:shadow-lg"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Save
                      </motion.button>
                    </div>
                  </form>
                </motion.div>
              </div>
            )}
            {showMedicalDetails && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
                >
                  <div className="p-6 border-b flex justify-between items-center bg-[#0077B6]/10">
                    <h2 className="text-xl font-semibold text-[#0077B6]">Medical Record Details</h2>
                    <button
                      onClick={() => setShowMedicalDetails(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Date</p>
                        <p className="text-sm text-gray-900">{formatDate(showMedicalDetails.date)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Chief Complaint</p>
                        <p className="text-sm text-gray-900">{showMedicalDetails.chiefComplaint}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Diagnosis</p>
                      <p className="text-sm text-gray-900">{showMedicalDetails.diagnosis}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Treatment</p>
                      <p className="text-sm text-gray-900">{showMedicalDetails.treatment}</p>
                    </div>
                    {showMedicalDetails.notes && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Notes</p>
                        <p className="text-sm text-gray-900">{showMedicalDetails.notes}</p>
                      </div>
                    )}
                    {showMedicalDetails.vitalSigns && (
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-2">Vital Signs</p>
                        <div className="grid grid-cols-2 gap-4">
                          {showMedicalDetails.vitalSigns.bloodPressure && (
                            <div>
                              <p className="text-xs text-gray-500">Blood Pressure</p>
                              <p className="text-sm text-gray-900">{showMedicalDetails.vitalSigns.bloodPressure}</p>
                            </div>
                          )}
                          {showMedicalDetails.vitalSigns.heartRate && (
                            <div>
                              <p className="text-xs text-gray-500">Heart Rate</p>
                              <p className="text-sm text-gray-900">{showMedicalDetails.vitalSigns.heartRate}</p>
                            </div>
                          )}
                          {showMedicalDetails.vitalSigns.temperature && (
                            <div>
                              <p className="text-xs text-gray-500">Temperature</p>
                              <p className="text-sm text-gray-900">{showMedicalDetails.vitalSigns.temperature}</p>
                            </div>
                          )}
                          {showMedicalDetails.vitalSigns.weight && (
                            <div>
                              <p className="text-xs text-gray-500">Weight</p>
                              <p className="text-sm text-gray-900">{showMedicalDetails.vitalSigns.weight}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end gap-3">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => deleteMedicalRecord(showMedicalDetails._id)}
                        className="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full hover:from-red-600 hover:to-red-700 font-medium transition-colors shadow-md"
                      >
                        Delete
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Medical; 